import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';

import { useGetQuotationQuery, useConvertQuotationToOrderMutation } from '../../../store/api/quotationsApi';
import { exportQuotationToPdf, shareQuotationPdf } from '../utils/exportQuotationPdf';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function QuotationDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const { data: quotation, isLoading, error } = useGetQuotationQuery(id);
  const { data: company } = useGetCurrentCompanyQuery();
  const [convertToOrder, { isLoading: converting }] = useConvertQuotationToOrderMutation();

  const currency = company?.currency || 'USD';
  const formatAmount = (v) => {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(Number(v || 0));
    } catch (e) {
      return `${currency} ${Number(v || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const canConvert = quotation && !['converted', 'rejected'].includes(quotation.status);
  const itemCount = quotation?.items?.length ?? 0;

  const handleConvertClick = () => setConvertDialogOpen(true);

  const handleConvertConfirm = async () => {
    try {
      const result = await convertToOrder(id).unwrap();
      setConvertDialogOpen(false);
      alert('Quotation converted to sales order. Stock has been deducted.');
      if (result?.salesOrder?.id) navigate(`/sales/orders/${result.salesOrder.id}`);
      else navigate('/sales');
    } catch (e) {
      alert(e?.data?.message || 'Failed to convert quotation');
    }
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error || !quotation) return <Typography color="error">Quotation not found</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/quotations')} sx={{ borderRadius: 2 }}>
            Back
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Quotation: {quotation.quotationNumber}
          </Typography>
          <Chip label={quotation.status} color={quotation.status === 'converted' ? 'success' : 'default'} size="small" sx={{ textTransform: 'capitalize' }} />
        </Box>
        {canConvert && (
          <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={handleConvertClick} disabled={converting} sx={{ borderRadius: 2 }}>
            {converting ? 'Converting...' : 'Convert to Sales Order'}
          </Button>
        )}
        <Button variant="outlined" color="secondary" startIcon={<PrintIcon />} onClick={() => exportQuotationToPdf(quotation, company)} sx={{ borderRadius: 2 }}>
          Print PDF
        </Button>
        <Button variant="outlined" color="info" startIcon={<ShareIcon />} onClick={() => shareQuotationPdf(quotation, company)} sx={{ borderRadius: 2 }}>
          Share
        </Button>
      </Box>

      {canConvert && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Before converting:</strong> The sales order will be created from the <strong>current saved version</strong> of this quotation.
            If the quotation has changed (e.g. customer requested different items or prices), edit the quotation first, then convert.
          </Typography>
        </Paper>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Customer</Typography>
              <Typography variant="h6">{quotation.customer?.name || '-'}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>Date: {quotation.quotationDate ? new Date(quotation.quotationDate).toLocaleDateString() : '-'}</Typography>
              {quotation.validUntil && (
                <Typography variant="body2">Valid until: {new Date(quotation.validUntil).toLocaleDateString()}</Typography>
              )}
              {quotation.notes && (
                <Typography variant="body2" sx={{ mt: 1 }}>Notes: {quotation.notes}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Totals</Typography>
              <Typography variant="h6" color="success.main">{formatAmount(quotation.grandTotal)}</Typography>
              <Typography variant="body2">Subtotal: {formatAmount(quotation.subtotal)}</Typography>
              <Typography variant="body2">Discount: {formatAmount(quotation.discountTotal)}</Typography>
              <Typography variant="body2">VAT: {formatAmount(quotation.vatTotal)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mt: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Qty</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Discount</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">VAT</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(quotation.items || []).map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.product?.name || '-'}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">{formatAmount(item.unitPrice)}</TableCell>
                  <TableCell align="right">{formatAmount(item.discount)}</TableCell>
                  <TableCell align="right">{formatAmount(item.vatAmount)}</TableCell>
                  <TableCell align="right">{formatAmount(item.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {quotation.status === 'draft' && (
        <Box sx={{ mt: 3 }}>
          <Button variant="outlined" onClick={() => navigate(`/quotations/${id}/edit`)} sx={{ borderRadius: 2 }}>
            Edit Quotation
          </Button>
        </Box>
      )}

      <Dialog open={convertDialogOpen} onClose={() => !converting && setConvertDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Convert to Sales Order</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            A new sales order will be created using the <strong>current saved quotation</strong> (customer, items, and totals shown above).
            Stock will be deducted. The quotation will be marked as converted and cannot be edited.
          </DialogContentText>
          <Typography variant="body2" color="text.secondary">
            Customer: <strong>{quotation?.customer?.name || '-'}</strong><br />
            Total: <strong>{formatAmount(quotation?.grandTotal)}</strong><br />
            Items: <strong>{itemCount} line(s)</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
            If you need to change anything, cancel and use &quot;Edit Quotation&quot; first, then convert again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)} disabled={converting}>Cancel</Button>
          <Button variant="contained" onClick={handleConvertConfirm} disabled={converting} startIcon={<CheckCircleIcon />}>
            {converting ? 'Converting...' : 'Confirm & Convert'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default QuotationDetail;
